import { useState } from "react";
import { MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Badge, fmtDate } from "@/components/Protected";

const REF_STATUSES = ["new", "reviewing", "underwriting", "boarded", "declined"];

export function ReferralItem({ r, onChange }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");

  const setStatus = async (status) => {
    try {
      await api.patch(`/referrals/${r.id}/status`, { status });
      onChange({ ...r, status });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addNote = async () => {
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/referrals/${r.id}/notes`, { text });
      onChange({ ...r, notes: [...(r.notes || []), data] });
      setText("");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const notes = Array.isArray(r.notes) ? r.notes : [];
  return (
    <article className="border border-white/10 bg-[#0d1524] p-5" data-testid={`referral-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--gold)]">
            {r.direction === "finbpm_to_midhq" ? "FinBPM → MidHq" : "MidHq → FinBPM"}
          </p>
          <h3 className="mt-1 font-display text-xl font-medium">{r.merchant_name}</h3>
          <p className="text-sm text-slate-300">{r.industry} · {r.origin_country} → {r.target_jurisdiction} · {r.monthly_volume}</p>
        </div>
        <Badge s={r.status} testid={`referral-status-${r.id}`} />
      </div>
      {r.details && <p className="mt-3 text-sm text-slate-300">{r.details}</p>}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4">
        <span className="font-mono text-[11px] text-slate-400">{r.submitted_by} · {r.submitted_company} · {fmtDate(r.created_at)}</span>
        <div className="flex items-center gap-2">
          <select className="field !w-auto !py-1.5 !text-xs" value={r.status} onChange={(e) => setStatus(e.target.value)} data-testid={`referral-status-select-${r.id}`}>
            {REF_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white" data-testid={`referral-notes-toggle-${r.id}`}>
            <MessageSquare className="h-3.5 w-3.5" /> {notes.length}
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-4 space-y-3" data-testid={`referral-notes-${r.id}`}>
          {notes.map((n, i) => (
            <div key={i} className={`border-l-2 pl-3 text-sm ${n.company === "FinBPM" ? "border-[var(--gold)]" : "border-sky-400"}`}>
              <p className="text-slate-200">{n.text}</p>
              <p className="font-mono text-[10px] text-slate-400">{n.author} · {n.company} · {fmtDate(n.created_at)}</p>
            </div>
          ))}
          <div className="flex gap-2">
            <input className="field !py-2 !text-sm" placeholder="Add a note for the other desk" value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid={`referral-note-input-${r.id}`} />
            <button onClick={addNote} className="grid w-11 place-items-center bg-white text-black hover:bg-[var(--gold)]" data-testid={`referral-note-send-${r.id}`}><Send className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </article>
  );
}
